import React, { useState, useEffect, useRef ,useMemo } from 'react';
import { Pencil, Info, Check, X, Loader2, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import UploadEducationModal from './UploadEducationalModal';
import tick from '../../assets/tick.png';
import pending from '../../assets/pending.png';
import cross from '../../assets/cross.png';
import pendingBatch from '../../assets/pendingBatch.png';
import verifiedBatch from '../../assets/verifiedBatch.png';
import notverifiedBatch from '../../assets/not_verified.svg'
import proofCertificate from '../../assets/proofCertificate.png';
import preview from '../../assets/preview.png';
import { SuccessModal, ErrorModal } from '../messagesModal';
import blacklist from "../../assets/blacklist.png";
import AccessoryManageModal  from './Accessorymanagemodal';
import KYWform from '../KYWform';
import useDocumentPreview from '../../hooks/useDocumentPreview';

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Build tooltip rows - generic function for most items
const buildTooltip = (statusValue, doneByName, doneAt) => [
  { label: "Status", value: statusValue },
  { label: "Done By", value: doneByName || "-" },
  { label: "Done At", value: formatDate(doneAt) },
];

// Build tooltip specifically for Bank Details with isBankNameException
const buildBankTooltip = (statusValue, doneByName, doneAt, isBankNameException) => [
  { label: "Status", value: statusValue },
  { label: "Done By", value: doneByName || "-" },
  { label: "Done At", value: formatDate(doneAt) },
  { label: "Bank Name Exception", value: isBankNameException ? "Yes" : "No" },
];

// Build tooltip for the blacklist badge - ONLY shows these fields
const buildBlacklistTooltip = (isBlacklisted, reason, markedByName, markedAt) => [
  { label: "Status", value: isBlacklisted ? "Blacklisted" : "Not Blacklisted" },
  { label: "Reason", value: reason || "-" },
  { label: "Marked By", value: markedByName || "-" },
  { label: "Marked At", value: formatDate(markedAt) },
];

// Build tooltip for documents with verification info
const buildDocumentTooltip = (docData) => {
  const rows = [];

  if (docData?.verifiedAt) {
    // Document has been reviewed — show verification info, not upload info
    rows.push({ label: "Status", value: docData.isVerified ? "Verified" : "Not Verified" });
    rows.push({ label: "Verified By", value: docData.verifiedByName || "-" });
    rows.push({ label: "Verified At", value: formatDate(docData.verifiedAt) });
  } else if (docData?.uploadedAt) {
    rows.push({ label: "Status", value: "Uploaded" });
    rows.push({ label: "Uploaded By", value: docData.uploadedByName || "-" });
    rows.push({ label: "Uploaded At", value: formatDate(docData.uploadedAt) });
  } else {
    rows.push({ label: "Status", value: "Not uploaded" });
  }

  return rows;
};

// Build tooltip for an individual allotted accessory
const buildAccessoryTooltip = (item) => [
  { label: "Status", value: item?.status || "-" },
  { label: "Amount", value: item?.AllotedAmount != null ? `₹${item.AllotedAmount}` : "-" },
  { label: "Alloted By", value: item?.AllotedByName || "-" },
  { label: "Alloted On", value: formatDate(item?.AllotedOn) },
];

const getBooleanStatus = (isDone) => (isDone ? "verified" : "pending");

// FIXED: This function now properly handles bank details verification status
const getTristateStatus = (statusValue) => {
  // If statusValue is undefined, null, or empty string, check if we have bank details
  if (!statusValue) {
    // If no status is provided, return "pending" by default
    // This will be overridden by the component logic when bank details exist
    return "pending";
  }

  const normalized = (statusValue || "").toString().toLowerCase();
  if (["success", "verified", "approved", "true"].includes(normalized)) return "verified";
  if (["rejected", "failed", "not verified", "not_verified"].includes(normalized)) return "rejected";
  return "pending";
};

const getDocumentStatus = (docMeta) => {
  if (!docMeta) return "pending";
  if (docMeta.isVerified) return "verified";
  if (docMeta.verifiedAt) return "rejected"; 
  return "pending"; 
};

// Status icon for an allotted accessory (based on its alloted/returned status)
const getAccessoryStatusIcon = (item) => {
  const status = (item?.status || "").toString().toLowerCase();
  if (status === "returned") return tick;
  if (status === "alloted") return pending;
  return pending;
};



const VerificationItem = ({
  label,
  verificationStatus=pending,
  onEdit = null,
  showEdit = true,
  editDisabledTitle = null,
  onView = null,
  showView = false,
  viewDisabled = true,
  tooltipRows = null,
  showTooltip = true,
}) => {
  const [showTip, setShowTip] = useState(false);

  const getStatusImage = () => {
    if (verificationStatus === "verified") return tick;
    if (verificationStatus === "rejected") return cross;
    return pending;
  };

  const hasTooltip = tooltipRows && tooltipRows.length > 0 && showTooltip;

  return (
    <div
      className="relative flex items-center justify-between gap-2 py-2.5 border-b border-gray-50 last:border-0"
      onMouseEnter={() => hasTooltip && setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div className="flex items-center gap-3 min-w-0">
        <img
          src={getStatusImage()}
          alt="status"
          className="h-4 w-4 flex-shrink-0 object-contain"
        />
        <span className="text-xs font-medium text-gray-600 truncate">{label}</span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {showView && (
          <img
            src={preview}
            alt="Preview"
            className={`h-3.5 w-3.5 object-contain ${
              !viewDisabled
                ? "cursor-pointer hover:opacity-70"
                : "opacity-30 cursor-not-allowed"
            }`}
            onClick={!viewDisabled ? onView : undefined}
          />
        )}
        {showEdit && (
  <Pencil
    className={`h-3 w-3 ${onEdit ? "text-indigo-500 cursor-pointer hover:text-indigo-700" : "text-gray-300 cursor-not-allowed"}`}
    onClick={onEdit || undefined}
    title={!onEdit && editDisabledTitle ? editDisabledTitle : undefined}
  />
)}
      </div>

      {hasTooltip && showTip && (
        <div className="absolute left-0 bottom-full mb-2 z-50 w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          {tooltipRows.map((row, i) => (
            <div key={i} className="flex justify-between gap-2 text-[11px] py-0.5">
              <span className="text-gray-400 font-medium whitespace-nowrap">{row.label}</span>
              <span className="text-gray-700 font-medium text-right break-words">{row.value ?? "-"}</span>
            </div>
          ))}
          <div className="absolute left-4 top-full w-2 h-2 -mt-1 rotate-45 bg-white border-r border-b border-gray-200" />
        </div>
      )}
    </div>
  );
};

// Document Verification Modal Component
const DocumentVerificationModal = ({
  isOpen,
  onClose,
  documentType,
  documentName,
  onVerify,
  isVerifying,
  onUpload
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 truncate pr-2">
            {documentName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-xs text-gray-500 mb-4 text-center">
            Review this document and confirm its verification status
          </p>

          {/* Prime focus: Verify / Not Verify */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onVerify(true)}
              disabled={isVerifying}
              className="flex flex-col items-center justify-center gap-2 py-5 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300 disabled:opacity-50 transition-colors"
            >
              {isVerifying ? (
                <Loader2 className="h-6 w-6 animate-spin text-green-600" />
              ) : (
                <Check className="h-6 w-6 text-green-600" />
              )}
              <span className="text-sm font-semibold text-green-700">Mark Verified</span>
            </button>

            <button
              onClick={() => onVerify(false)}
              disabled={isVerifying}
              className="flex flex-col items-center justify-center gap-2 py-5 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 disabled:opacity-50 transition-colors"
            >
              {isVerifying ? (
                <Loader2 className="h-6 w-6 animate-spin text-red-600" />
              ) : (
                <X className="h-6 w-6 text-red-600" />
              )}
              <span className="text-sm font-semibold text-red-700">Mark Not Verified</span>
            </button>
          </div>

          {/* Secondary action */}
          <button
            onClick={onUpload}
            disabled={isVerifying}
            className="w-full mt-4 px-4 py-2 text-xs text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors"
          >
            <Upload className="h-3.5 w-3.5" />
            Re-upload document
          </button>
        </div>
      </div>
    </div>
  );
};

const BankDetailsModal = ({ isOpen, onClose, bankDetails }) => {
  if (!isOpen) return null;
  
  const {
    bankName,        
    accountNumber,
    IFSC,
    bank_name,      
    branchName,
    branchAddress,
    city,
    district,
    state
  } = bankDetails || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-900">Bank Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-400">Account Holder Name</p>
            <p className="text-sm font-medium text-gray-800 break-words">{bankName || "-"}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400">Bank Name</p>
            <p className="text-sm font-medium text-gray-800 break-words">{bank_name || "-"}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400">Account Number</p>
            <p className="text-sm font-medium text-gray-800 break-words">{accountNumber || "-"}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400">IFSC Code</p>
            <p className="text-sm font-medium text-gray-800 break-words">{IFSC || "-"}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400">Branch Name</p>
            <p className="text-sm font-medium text-gray-800 break-words">{branchName || "-"}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400">Branch Address</p>
            <p className="text-sm font-medium text-gray-800 break-words">{branchAddress || "-"}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-400">City</p>
              <p className="text-sm font-medium text-gray-800 break-words">{city || "-"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400">District</p>
              <p className="text-sm font-medium text-gray-800 break-words">{district || "-"}</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400">State</p>
            <p className="text-sm font-medium text-gray-800 break-words">{state || "-"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const VerificationSection = ({
  verificationData,
  canEditVerification,
  onEditSection,
  onShowProofOfWork,
  isLoadingReport = false,
  onRefresh,
  fetchWorkerDetails,
}) => {
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentType, setDocumentType] = useState('marksheet');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
const [showErrorModal, setShowErrorModal] = useState(false);
const [modalMessage, setModalMessage] = useState("");
  const [documentStatus, setDocumentStatus] = useState({
    marksheet: false,
    resume: false,
    policeVerification: false,
    others: [],
  });
  const [documentUrls, setDocumentUrls] = useState({
    marksheet: null,
    resume: null,
    policeVerification: null,
    others: [],
  });
  const [documentMeta, setDocumentMeta] = useState({
    marksheet: { uploadedAt: null, uploadedBy: null, uploadedByName: null, isVerified: false, verifiedAt: null, verifiedBy: null, verifiedByName: null },
    resume: { uploadedAt: null, uploadedBy: null, uploadedByName: null, isVerified: false, verifiedAt: null, verifiedBy: null, verifiedByName: null },
    policeVerification: { uploadedAt: null, uploadedBy: null, uploadedByName: null, isVerified: false, verifiedAt: null, verifiedBy: null, verifiedByName: null },
    others: [],
  });
  const [othersDocumentTypes, setOthersDocumentTypes] = useState([]);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyDocumentType, setVerifyDocumentType] = useState('');
  const [verifyDocumentName, setVerifyDocumentName] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [selectedOthersDocIndex, setSelectedOthersDocIndex] = useState(null);
  const [isOthersDropdownOpen, setIsOthersDropdownOpen] = useState(false);
  const [isAccessoriesDropdownOpen, setIsAccessoriesDropdownOpen] = useState(false);
  const [showBatchTip, setShowBatchTip] = useState(false);
  const [showBlacklistTip, setShowBlacklistTip] = useState(false);
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
   const [showAccessoryModal, setShowAccessoryModal] = useState(false);
   const [showKYWModal, setShowKYWModal] = useState(false);
  const othersDropdownRef = useRef(null);
  const accessoriesDropdownRef = useRef(null);
  const { openDocumentPreview } = useDocumentPreview();

  const {
    isAadhaarVerified,
    aadhaarVerifiedByName,
    aadhaarVerifiedAt,
    employers,
    isReferenceChecked,
    isKycDone,
    takenApron,
    takenApronDate,
    bankName,
    accountNumber,
    IFSC,
    workerId,
    workerName,
    workerCode,
    verification,
    otherDocuments = [],
    // Blacklist fields
    isBlacklisted,
    eligibleCompanyName,
    eligibleLocation,
    blacklistReason,
    blacklistMarkedByName,
    blacklistMarkedAt,
    bankDetails,
    // Allotted accessories (logo, name, price)
    allottedAccessories = [],
  } = verificationData;

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Fetch others document types
  useEffect(() => {
    const fetchOthersDocumentTypes = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        if (!token) return;

        const response = await fetch(`${BASE_URL}/lenskart/others-document-types`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setOthersDocumentTypes(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching others document types:', error);
      }
    };

    fetchOthersDocumentTypes();
  }, [BASE_URL]);

  // Initialize document status + meta from verification data
  useEffect(() => {
    if (!verification) return;

    const applyDoc = (key, docObj) => {
      if (!docObj?.url) return;
      setDocumentStatus((prev) => ({ ...prev, [key]: true }));
      setDocumentUrls((prev) => ({ ...prev, [key]: docObj.url }));
      setDocumentMeta((prev) => ({
        ...prev,
        [key]: {
          uploadedAt: docObj.uploadedAt || null,
          uploadedBy: docObj.uploadedBy || null,
          uploadedByName: docObj.uploadedByName || null,
          isVerified: docObj.isVerified || false,
          verifiedAt: docObj.verifiedAt || null,
          verifiedBy: docObj.verifiedBy || null,
          verifiedByName: docObj.verifiedByName || null,
        },
      }));
    };

    applyDoc('marksheet', verification.educationVerification);
    applyDoc('resume', verification.resume); 
    applyDoc('policeVerification', verification.policeVerification);

    // Handle otherDocuments from the main response
    if (otherDocuments && Array.isArray(otherDocuments) && otherDocuments.length > 0) {
      console.log('Setting other documents:', otherDocuments);
      setDocumentStatus(prev => ({ ...prev, others: otherDocuments.map(doc => !!doc.url) }));
      setDocumentUrls(prev => ({ ...prev, others: otherDocuments.map(doc => doc.url || null) }));
      setDocumentMeta(prev => ({
        ...prev,
        others: otherDocuments.map(doc => ({
          uploadedAt: doc.uploadedAt || null,
          uploadedBy: doc.uploadedBy || null,
          uploadedByName: doc.uploadedByName || null,
          isVerified: doc.isVerified || false,
          verifiedAt: doc.verifiedAt || null,
          verifiedBy: doc.verifiedBy || null,
          verifiedByName: doc.verifiedByName || null,
          documentName: doc.name || 'Other Document',
          _id: doc._id || null,
        })),
      }));
    }
  }, [verification, otherDocuments]);

  // Close the "others" popover when clicking outside of it
  useEffect(() => {
    if (!isOthersDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (othersDropdownRef.current && !othersDropdownRef.current.contains(event.target)) {
        setIsOthersDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOthersDropdownOpen]);

  // Close the "accessories" popover when clicking outside of it
  useEffect(() => {
    if (!isAccessoriesDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (accessoriesDropdownRef.current && !accessoriesDropdownRef.current.contains(event.target)) {
        setIsAccessoriesDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAccessoriesDropdownOpen]);

  const handleDocumentUpload = (type) => {
    setDocumentType(type);
    setShowDocumentModal(true);
  };

  const handleDocumentSuccess = (data) => {
    if (documentType === 'others') {
      // Add new others document
      setDocumentStatus(prev => ({
        ...prev,
        others: [...prev.others, true]
      }));
      setDocumentUrls(prev => ({
        ...prev,
        others: [...prev.others, data?.url || null]
      }));
      setDocumentMeta(prev => ({
        ...prev,
        others: [...prev.others, {
          uploadedAt: data?.uploadedAt || null,
          uploadedBy: data?.uploadedBy || null,
          uploadedByName: data?.uploadedByName || null,
          isVerified: data?.isVerified || false,
          verifiedAt: data?.verifiedAt || null,
          verifiedBy: data?.verifiedBy || null,
          verifiedByName: data?.verifiedByName || null,
          documentName: data?.documentTypeName || 'Other Document',
          _id: data?._id || null,
        }]
      }));
    } else {
      setDocumentStatus((prev) => ({ ...prev, [documentType]: true }));
      setDocumentUrls((prev) => ({ ...prev, [documentType]: data?.url || null }));
      setDocumentMeta((prev) => ({
        ...prev,
        [documentType]: {
          uploadedAt: data?.uploadedAt || null,
          uploadedBy: data?.uploadedBy || null,
          uploadedByName: data?.uploadedByName || null,
          isVerified: data?.isVerified || false,
          verifiedAt: data?.verifiedAt || null,
          verifiedBy: data?.verifiedBy || null,
          verifiedByName: data?.verifiedByName || null,
        },
      }));
    }

    console.log(`${documentType} uploaded successfully:`, data);

    // Close the upload modal
    setShowDocumentModal(false);
    if (onRefresh) onRefresh(); 
  };

  const handleViewDocument = (url) => openDocumentPreview(url);

  const handleVerifyDocument = (type, index = null) => {
    // Check if document exists
    if (type === 'others' && index !== null) {
      if (!documentUrls.others[index]) {
        // If no document, open upload modal
        handleDocumentUpload('others');
        return;
      }
    } else if (type !== 'others' && !documentUrls[type]) {
      // If no document, open upload modal
      handleDocumentUpload(type);
      return;
    }

    setVerifyDocumentType(type);
    setSelectedOthersDocIndex(index);

    let docName = '';
    if (type === 'marksheet') docName = 'Educational Certificate';
    else if (type === 'resume') docName = 'Resume';
    else if (type === 'policeVerification') docName = 'Police Verification';
    else if (type === 'others' && index !== null) {
      const docMeta = documentMeta.others[index];
      docName = docMeta?.documentName || 'Other Document';
    }
    setVerifyDocumentName(docName);
    setShowVerifyModal(true);
  };

  const handleVerifyConfirm = async (isVerified) => {
    setIsVerifying(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      if (!token) {
        throw new Error('Authentication required');
      }

      const documentName =
  verifyDocumentType === 'others' && selectedOthersDocIndex !== null
    ? documentMeta.others[selectedOthersDocIndex]?.documentName || verifyDocumentName
    : 'Admin';

      const body = {
  documentType: verifyDocumentType,
  isVerified: isVerified,
  name: documentName,
};

      const response = await fetch(`${BASE_URL}/lenskart-admin/verify-worker-document/${workerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update local state
        if (verifyDocumentType === 'others' && selectedOthersDocIndex !== null) {
          setDocumentMeta(prev => {
            const newOthers = [...prev.others];
            newOthers[selectedOthersDocIndex] = {
              ...newOthers[selectedOthersDocIndex],
              isVerified: isVerified,
              verifiedAt: new Date().toISOString(),
              verifiedBy: 'Admin',
              verifiedByName: 'Admin',
            };
            return { ...prev, others: newOthers };
          });
        } else {
          setDocumentMeta(prev => ({
            ...prev,
            [verifyDocumentType]: {
              ...prev[verifyDocumentType],
              isVerified: isVerified,
              verifiedAt: new Date().toISOString(),
              verifiedBy: 'Admin',
              verifiedByName: 'Admin',
            },
          }));
        }
        setShowVerifyModal(false);
        setModalMessage(`Document ${isVerified ? 'verified' : 'marked as not verified'} successfully!`);
        setShowSuccessModal(true);
        if (onRefresh) onRefresh();
      } else {
        throw new Error(data.message || 'Failed to verify document');
      }
    } catch (error) {
      console.error('Error verifying document:', error);
      setModalMessage(error.message || 'Failed to verify document. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const toggleOthersDropdown = () => {
    setIsOthersDropdownOpen((prev) => !prev);
  };

  const toggleAccessoriesDropdown = () => {
    setIsAccessoriesDropdownOpen((prev) => !prev);
  };

  const refCheck = verification?.RefCheck;
  const kyc = verification?.isKycDone;
  const nameVerification = verification?.nameVerification;
  const lastEmployerCheck = employers?.[employers.length - 1];

  const isBankVerified = getTristateStatus(nameVerification?.status) === "verified";
  const isMarksheetVerified = getDocumentStatus(documentMeta.marksheet) === "verified";
  const isResumeVerified = getDocumentStatus(documentMeta.resume) === "verified";
  const isPoliceVerified = getDocumentStatus(documentMeta.policeVerification) === "verified";
  const areOthersVerified =
    documentMeta.others.length === 0 || documentMeta.others.every((doc) => doc.isVerified);

  const allVerificationsDone =
    isAadhaarVerified &&
    employers?.length > 0 &&
    isMarksheetVerified &&
    isPoliceVerified &&
    isResumeVerified &&
    isReferenceChecked &&
    isKycDone &&
    takenApron &&
    isBankVerified &&
    areOthersVerified;

  // Overall badge status is driven by nameVerification?.status directly ("success" -> verified badge),
  // rather than requiring every individual check (allVerificationsDone) to be true.
const overallVerificationStatus = getTristateStatus(nameVerification?.status);

const isOverallVerified = overallVerificationStatus === "success";

  const othersCount = documentMeta.others.length;
  const accessoriesCount = allottedAccessories.length;

  const getOtherDocStatusIcon = (docMeta) => {
  if (docMeta.isVerified) return tick;
  if (docMeta.verifiedAt) return cross; 
  if (docMeta.uploadedAt) return pending;
  return pending;
};

const buildAadhaarTooltip = (isVerified, verifiedByName, verifiedOn) => [
  { label: "Status", value: isVerified ? "Verified" : "Pending" },
  { label: "Verified By", value: verifiedByName || "-" },
  { label: "Verified On", value: formatDate(verifiedOn) },
];

const handleViewBankDetails = () => {
  setShowBankDetailsModal(true);
};

  const buildBankAccountTooltip = (bankName, accountNumber, IFSC) => [
  { label: "Account Holder", value: bankName || "-" },  // This is the account holder name
  { label: "Account Number", value: accountNumber || "-" },
  { label: "IFSC", value: IFSC || "-" },
];

  // FIXED: Determine bank verification status
  // If bank details exist and nameVerification status is "success" or "verified", show as verified
  // If bank details exist but no verification status yet, show as pending (needs verification)
  // If no bank details at all, show as pending
const getBankVerificationStatus = () => {
  const hasBankDetails = Object.values(bankDetails || {}).some(
    (value) => value !== null && value !== undefined && value !== ""
  );

  return hasBankDetails ? "verified" : "rejected";
};

  return (
    <>
      <div style={{ backgroundColor: '#F6FBFF' }} className="rounded-2xl shadow-sm border border-gray-200 p-5 h-full flex-1 flex flex-col">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3 border-b border-gray-100 pb-2.5">
          <div className="flex items-center gap-3 min-w-0">
            {/* Overall verification badge */}
<div
  className="relative flex items-center gap-2 min-w-0 leading-none"
  onMouseEnter={() => setShowBatchTip(true)}
  onMouseLeave={() => setShowBatchTip(false)}
>
  <div className="flex items-center gap-2">
<img
  src={
    nameVerification?.status === "success"
      ? verifiedBatch
      : nameVerification?.status === "failed"
      ? notverifiedBatch
      : pendingBatch
  }
  alt={
    nameVerification?.status === "success"
      ? "Verified"
      : nameVerification?.status === "failed"
      ? "Not Verified"
      : "Pending"
  }
  className="h-6 w-auto object-contain"
/>
    {canEditVerification && (
      <Pencil
        className="h-4 w-4 text-indigo-500 cursor-pointer hover:text-indigo-700 flex-shrink-0"
        onClick={() => onEditSection('bank')}
      />
    )}
  </div>

  {showBatchTip && (
    <div className="absolute left-0 top-full mt-2 z-50 w-60 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      {buildBankTooltip(
        nameVerification?.status || (bankName ? "Added" : "Pending"),
        nameVerification?.doneByName,
        nameVerification?.doneAt,
        nameVerification?.isBankNameException || false
      ).map((row, i) => (
        <div key={i} className="flex justify-between gap-2 text-[11px] py-0.5">
          <span className="text-gray-400 font-medium whitespace-nowrap">{row.label}</span>
          <span className="text-gray-700 font-medium text-right break-words">{row.value ?? "-"}</span>
        </div>
      ))}
      <div className="absolute left-4 bottom-full w-2 h-2 mb-1 rotate-45 bg-white border-l border-t border-gray-200" />
    </div>
  )}
</div>

            {/* Blacklist badge — shows "Blacklisted" or "Not Blacklisted" with pencil inside */}
            <div
  className="relative flex items-center gap-2 min-w-0 leading-none"
  onMouseEnter={() => {
    // ONLY show tooltip when blacklisted
    if (isBlacklisted) {
      setShowBlacklistTip(true);
    }
  }}
  onMouseLeave={() => setShowBlacklistTip(false)}
>
  <div className="flex items-center gap-2">
    {isBlacklisted ? (
      // Show blacklisted badge with edit icon
      <>
        <span
          className="flex items-center justify-center text-[11px] font-bold px-3.5 py-2 rounded-full whitespace-nowrap border transition-colors duration-200 bg-black text-white border-black"
        >
          Blacklisted
        </span>
        {canEditVerification && (
          <Pencil
            className="h-4 w-4 text-indigo-500 cursor-pointer hover:text-indigo-700 flex-shrink-0"
            onClick={() => onEditSection('blacklist')}
          />
        )}
      </>
    ) : (
      // Show blacklist icon when not blacklisted (no edit icon)
      canEditVerification && (
        <div
          className="relative group cursor-pointer"
          onClick={() => onEditSection('blacklist')}
        >
          <img
            src={blacklist}
            alt="Blacklist"
            className="h-8 w-8 object-contain hover:opacity-70 transition-opacity"
          />
          {/* Tooltip on hover */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50">
            <div className="bg-gray-800 text-white text-xs px-3 py-1.5 rounded whitespace-nowrap shadow-lg">
              Blacklist this candidate
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 -mt-1 rotate-45 bg-gray-800" />
          </div>
        </div>
      )
    )}
  </div>

  {/* Tooltip ONLY shows when blacklisted */}
  {isBlacklisted && showBlacklistTip && (
    <div className="absolute left-0 top-full mt-2 z-50 w-60 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      {buildBlacklistTooltip(
        isBlacklisted,
        blacklistReason,
        blacklistMarkedByName,
        blacklistMarkedAt
      ).map((row, i) => (
        <div key={i} className="flex justify-between gap-2 text-[11px] py-0.5">
          <span className="text-gray-400 font-medium whitespace-nowrap">{row.label}</span>
          <span className="text-gray-700 font-medium text-right break-words">{row.value ?? "-"}</span>
        </div>
      ))}
      <div className="absolute left-4 bottom-full w-2 h-2 mb-1 rotate-45 bg-white border-l border-t border-gray-200" />
    </div>
  )}
</div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              onClick={!isLoadingReport ? onShowProofOfWork : undefined}
              className={`flex items-center gap-1 text-indigo-600 text-[10px] font-medium cursor-pointer border border-indigo-200 px-1.5 py-1 rounded-full bg-indigo-50 whitespace-nowrap ${
                isLoadingReport ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-100 transition-colors'
              }`}
            >
              {isLoadingReport ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <img src={proofCertificate} alt="Proof of Work" className="h-4 w-4 flex-shrink-0 object-contain" />
                  <span>Proof of Work</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 flex-1 content-center">
          {/* Left Column */}
          <div className="md:pr-6">
            <div>
              <VerificationItem
  label="Aadhaar Verified"
  verificationStatus={getBooleanStatus(isAadhaarVerified)}
  onEdit={() => onEditSection('aadhaar')}
  tooltipRows={buildAadhaarTooltip(isAadhaarVerified, aadhaarVerifiedByName, aadhaarVerifiedAt)}
/>

              <VerificationItem
                label="Company Check"
                verificationStatus={getBooleanStatus(employers?.length > 0)}
                onEdit={() => onEditSection('company')}
                tooltipRows={buildTooltip(employers?.length > 0 ? "Checked" : "Pending", lastEmployerCheck?.doneByName, lastEmployerCheck?.doneAt)}
              />

              <VerificationItem
                label="Educational Certificate"
                verificationStatus={getDocumentStatus(documentMeta.marksheet)}
                onEdit={canEditVerification ? () => handleVerifyDocument('marksheet') : null}
editDisabledTitle="Not permitted"
                showView={true}
                viewDisabled={!documentUrls.marksheet}
                onView={() => handleViewDocument(documentUrls.marksheet)}
                tooltipRows={buildDocumentTooltip(documentMeta.marksheet)}
              />

              <VerificationItem
                label="Police Verification"
                verificationStatus={getDocumentStatus(documentMeta.policeVerification)}
                onEdit={canEditVerification ? () => handleVerifyDocument('policeVerification') : null}
                showView={true}
                viewDisabled={!documentUrls.policeVerification}
                onView={() => handleViewDocument(documentUrls.policeVerification)}
                tooltipRows={buildDocumentTooltip(documentMeta.policeVerification)}
              />

              <VerificationItem
                label="Resume"
                verificationStatus={getDocumentStatus(documentMeta.resume)}
                onEdit={canEditVerification ? () => handleVerifyDocument('resume') : null}
                showView={true}
                viewDisabled={!documentUrls.resume}
                onView={() => handleViewDocument(documentUrls.resume)}
                tooltipRows={buildDocumentTooltip(documentMeta.resume)}
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="md:pl-6 md:border-l md:border-gray-200">
            <div>
              <VerificationItem
                label="Reference Check"
                verificationStatus={getBooleanStatus(isReferenceChecked)}
                onEdit={() => onEditSection('reference')}
                tooltipRows={buildTooltip(isReferenceChecked ? "Checked" : "Pending", refCheck?.doneByName, refCheck?.doneAt)}
              />

<VerificationItem
  label="KYW"
  verificationStatus={getBooleanStatus(isKycDone)}
  onEdit={canEditVerification ? () => setShowKYWModal(true) : null}
  showEdit={false}
  tooltipRows={buildTooltip(kyc.isDone ? "Done" : "Pending", kyc?.doneByName, kyc?.doneAt)}
/>

              {/* Accessories - shown as a list, like Other Documents (logo, name, price) */}
              <div ref={accessoriesDropdownRef} className="relative border-b border-gray-50 last:border-0 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={accessoriesCount > 0 ? tick : pending}
                      alt="status"
                      className="h-4 w-4 flex-shrink-0 object-contain"
                    />
                    <span className="text-xs font-medium text-gray-600 truncate">Essentials</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {accessoriesCount > 0 && (
                      <button
                        onClick={toggleAccessoriesDropdown}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={isAccessoriesDropdownOpen ? "Collapse" : "Expand"}
                      >
                        {isAccessoriesDropdownOpen ? (
                          <ChevronUp className="h-4 w-4 text-gray-600" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                    )}
                    <Pencil
                      className="h-3 w-3 text-indigo-500 cursor-pointer hover:text-indigo-700"
                      onClick={() => {
                        setIsAccessoriesDropdownOpen(false);
                        setShowAccessoryModal(true);
                      }}
                    />
                  </div>
                </div>

                {/* Single accessory: render inline, no popover needed */}
                {/* {accessoriesCount === 1 && (
                  <div className="mt-2 space-y-1">
                    {allottedAccessories.map((item, index) => (
                      <div
                        key={item._id || index}
                        className="flex items-center justify-between pl-7 pr-2 py-1 bg-gray-50 rounded"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <img
                            src={item.accessory?.logo}
                            alt={item.accessory?.name || "Accessory"}
                            className="h-4 w-4 object-contain rounded-full flex-shrink-0"
                          />
                          <span className="text-xs text-gray-600 truncate">
                            {item.accessory?.name || "Accessory"}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-gray-700 flex-shrink-0">
                          ₹{item.AllotedAmount ?? item.accessory?.defaultPrice ?? 0}
                        </span>
                      </div>
                    ))}
                  </div>
                )} */}

                {/* Multiple accessories: floating popover below the row — does NOT push layout down */}
                {accessoriesCount > 0 && isAccessoriesDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-gray-200 bg-white shadow-lg p-2 space-y-1">
                    {allottedAccessories.map((item, index) => (
                      <div
                        key={item._id || index}
                        className="flex items-center justify-between pl-2 pr-2 py-1.5 bg-gray-50 rounded"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <img
                            src={item.accessory?.logo}
                            alt={item.accessory?.name || "Accessory"}
                            className="h-4 w-4  rounded-full flex-shrink-0"
                          />
                          <span className="text-xs text-gray-600 truncate">
                            {item.accessory?.name || "Accessory"}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-gray-700 flex-shrink-0">
                          ₹{item.AllotedAmount ?? item.accessory?.defaultPrice ?? 0}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bank Details */}
              <VerificationItem
    label="Bank Details"
    verificationStatus={getBankVerificationStatus()}
    showEdit={false}
    showView={true}
    viewDisabled={!bankName && !accountNumber && !IFSC}
    onView={handleViewBankDetails} 
    tooltipRows={buildBankAccountTooltip(bankName, accountNumber, IFSC)}
  />

              {/* Others Documents - Show as list */}
              <div ref={othersDropdownRef} className="relative border-b border-gray-50 last:border-0 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={
                        documentMeta.others.length > 0 && documentMeta.others.every((doc) => doc.isVerified)
                          ? tick
                          : pending
                      }
                      alt="status"
                      className="h-4 w-4 flex-shrink-0 object-contain"
                    />
                    <span className="text-xs font-medium text-gray-600 truncate">Other Documents</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {othersCount > 1 && (
                      <button
                        onClick={toggleOthersDropdown}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={isOthersDropdownOpen ? "Collapse" : "Expand"}
                      >
                        {isOthersDropdownOpen ? (
                          <ChevronUp className="h-4 w-4 text-gray-600" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                    )}
                    <Pencil
                      className={`h-3 w-3 ${
                        canEditVerification ? "text-indigo-500 cursor-pointer hover:text-indigo-700" : "text-gray-300 cursor-not-allowed"
                      }`}
                      onClick={canEditVerification ? () => { setIsOthersDropdownOpen(false); handleDocumentUpload('others'); } : undefined}
                      title={!canEditVerification ? "Not permitted" : undefined}
                    />
                  </div>
                </div>

                {/* Single doc: render inline, no popover needed */}
                {othersCount === 1 && (
                  <div className="mt-2 space-y-1">
                    {documentMeta.others.map((doc, index) => {
                      const docLabel = doc.documentName || 'Other Document';
                      const hasUrl = documentUrls.others[index];

                      return (
                        <div key={doc._id || index} className="flex items-center justify-between pl-7 pr-2 py-1 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <img
                              src={getOtherDocStatusIcon(doc)}
                              alt="status"
                              className="h-3.5 w-3.5 object-contain"
                            />
                            <span className="text-xs text-gray-600">{docLabel}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {hasUrl && (
                              <img
                                src={preview}
                                alt="Preview"
                                className="h-3 w-3 object-contain cursor-pointer hover:opacity-70"
                                onClick={() => handleViewDocument(documentUrls.others[index])}
                              />
                            )}
                            {hasUrl && (
  <Check
    className={`h-3 w-3 ${
      canEditVerification
        ? `cursor-pointer ${doc.isVerified ? "text-green-500" : "text-gray-400 hover:text-green-600"}`
        : "text-gray-200 cursor-not-allowed"
    }`}
    onClick={canEditVerification ? () => handleVerifyDocument('others', index) : undefined}
    title={!canEditVerification ? "Not permitted" : undefined}
  />
)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Multiple docs: floating popover below the row — does NOT push layout down */}
                {othersCount > 1 && isOthersDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-gray-200 bg-white shadow-lg p-2 space-y-1">
                    {documentMeta.others.map((doc, index) => {
                      const docLabel = doc.documentName || 'Other Document';
                      const hasUrl = documentUrls.others[index];

                      return (
                        <div key={doc._id || index} className="flex items-center justify-between pl-2 pr-2 py-1.5 bg-gray-50 rounded">
                          <div className="flex items-center gap-2 min-w-0">
                            <img
                              src={getOtherDocStatusIcon(doc)}
                              alt="status"
                              className="h-3.5 w-3.5 object-contain flex-shrink-0"
                            />
                            <span className="text-xs text-gray-600 truncate">{docLabel}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {hasUrl && (
                              <img
                                src={preview}
                                alt="Preview"
                                className="h-3 w-3 object-contain cursor-pointer hover:opacity-70"
                                onClick={() => handleViewDocument(documentUrls.others[index])}
                              />
                            )}
                            {canEditVerification && hasUrl && (
                              <Check
                                className={`h-3 w-3 cursor-pointer ${
                                  doc.isVerified ? "text-green-500" : "text-gray-400 hover:text-green-600"
                                }`}
                                onClick={() => handleVerifyDocument('others', index)}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UploadEducationModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        workerId={workerId}
        documentType={documentType}
        onSuccess={handleDocumentSuccess}
        othersDocumentTypes={othersDocumentTypes}
      />

      <DocumentVerificationModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        documentType={verifyDocumentType}
        documentName={verifyDocumentName}
        onVerify={handleVerifyConfirm}
        isVerifying={isVerifying}
        onUpload={() => {
          setShowVerifyModal(false);
          handleDocumentUpload(verifyDocumentType);
        }}
      />

      <BankDetailsModal
  isOpen={showBankDetailsModal}
  onClose={() => setShowBankDetailsModal(false)}
  bankDetails={bankDetails || {
    bankName: bankName,
    accountNumber: accountNumber,
    IFSC: IFSC,
    bank_name: bankDetails?.bank_name || bankName,
    branchName: bankDetails?.branchName,
    branchAddress: bankDetails?.branchAddress,
    city: bankDetails?.city,
    district: bankDetails?.district,
    state: bankDetails?.state
  }}
/>
<AccessoryManageModal
  isOpen={showAccessoryModal}
  onClose={() => setShowAccessoryModal(false)}
  workerId={workerId}
  workerName={workerName}
  workerCode={workerCode}
onRefresh={onRefresh}
/>

{/* <KYWform
  isOpen={showKYWModal}
  onClose={() => setShowKYWModal(false)}
   workerId={workerId}
/> */}

<SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={modalMessage}
      />
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={modalMessage}
      />
    </>
  );
};

export default VerificationSection;